// (c) Copyright Ascensio System SIA 2009-2025
//
// This program is a free software product.
// You can redistribute it and/or modify it under the terms
// of the GNU Affero General Public License (AGPL) version 3 as published by the Free Software
// Foundation. In accordance with Section 7(a) of the GNU AGPL its Section 15 shall be amended
// to the effect that Ascensio System SIA expressly excludes the warranty of non-infringement of
// any third-party rights.
//
// This program is distributed WITHOUT ANY WARRANTY, without even the implied warranty
// of MERCHANTABILITY or FITNESS FOR A PARTICULAR  PURPOSE. For details, see
// the GNU AGPL at: http://www.gnu.org/licenses/agpl-3.0.html
//
// You can contact Ascensio System SIA at Lubanas st. 125a-25, Riga, Latvia, EU, LV-1021.
//
// The  interactive user interfaces in modified source and object code versions of the Program must
// display Appropriate Legal Notices, as required under Section 5 of the GNU AGPL version 3.
//
// Pursuant to Section 7(b) of the License you must retain the original Product logo when
// distributing the program. Pursuant to Section 7(e) we decline to grant you any rights under
// trademark law for use of our trademarks.
//
// All the Product's GUI elements, including illustrations and icon sets, as well as technical writing
// content are licensed under the terms of the Creative Commons Attribution-ShareAlike 4.0
// International. See the License terms at http://creativecommons.org/licenses/by-sa/4.0/legalcode

import { decode } from "he";
import { useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import ArrowIcon from "PUBLIC_DIR/images/arrow.react.svg";
import AvatarBaseReactSvgUrl from "PUBLIC_DIR/images/avatar.base.react.svg?url";
import AvatarDarkReactSvgUrl from "PUBLIC_DIR/images/avatar.dark.react.svg?url";

import RoleHistories from "../role-histories/RoleHistories";
import { useTheme } from "../../../../hooks/useTheme";

import styles from "./RoleStep.module.scss";
import type { RoleStepProps } from "./RoleStep.types";

const RoleStep = ({
  user,
  processStatus,
  roleName,
  histories,
  currentUserId,
  stoppedBy,
  withHistory = true,
}: RoleStepProps) => {
  const [collapsed, setCollapsed] = useState(true);
  const { isBase } = useTheme();
  const { t } = useTranslation("Common");

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const hasMoreThanOneHistory = histories.length > 1;

  const lastHistory = histories.at(-1);

  const history =
    hasMoreThanOneHistory && collapsed
      ? lastHistory
        ? [lastHistory]
        : []
      : histories;

  const hasAvatar = user.hasAvatar;

  const userName = decode(user.displayName);

  return (
    <div data-process-status={processStatus} className={styles.roleContainer}>
      <div className={styles.role}>
        <picture className={styles.avatarContainer}>
          <img
            className={styles.avatar}
            src={
              hasAvatar
                ? user.avatar
                : isBase
                  ? AvatarBaseReactSvgUrl
                  : AvatarDarkReactSvgUrl
            }
            alt={user.userName}
          />
        </picture>
        <h5 title={roleName} className={styles.roleTitle}>
          {roleName}
        </h5>
        <p className={styles.userName}>
          {userName}{" "}
          {currentUserId === user.id ? `(${t("Common:MeLabel")})` : null}
        </p>
        {hasMoreThanOneHistory ? (
          <ArrowIcon
            onClick={toggleCollapse}
            className={classNames(styles.arrow, {
              [styles.rotated]: collapsed,
            })}
          />
        ) : null}
      </div>
      {withHistory ? (
        <RoleHistories
          className={styles.roleHistories}
          histories={history}
          stoppedBy={stoppedBy}
        />
      ) : null}
    </div>
  );
};

export default RoleStep;
